import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { ApolloLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { AuthenticationService } from '@ih/app/client/shared/services';
import { TrackerLink } from '@ih/app/client/shared/utils';
import {
  offlineLinkTracker,
  jobLinkTracker,
} from '@ih/app/client/shared/utils';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import SerializingLink from 'apollo-link-serialize';
import { LocalStorageWrapper, persistCache } from 'apollo3-cache-persist';

import { environment } from '../environments/environment';
import { CombinedGraphQLErrors } from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';

const URL = `${environment.api.url}`;
export const offlineTracker = offlineLinkTracker;
export const jobTrackerLink = jobLinkTracker;

export const errorLink = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.map(({ message, locations, path }) =>
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );
  } else if (error) {
    console.error(`[Network error]: ${error}`);
  }
});
export function createApollo(
  httpLink: HttpLink,
  authenticationService: AuthenticationService,
) {
  const serializingLink = new SerializingLink();
  const trackerLink = new TrackerLink();
  const cache = new InMemoryCache({
    typePolicies: {
      Api: { merge: true },
      ApiKey: { merge: true },
      App: { merge: true },
      BatchPayload: { merge: true },
      Board: { merge: true },
      BoardColumn: { merge: true },
      Card: { merge: true },
      Chart: { merge: true },
      Column: { merge: true },
      Dashboard: { merge: true },
      Data: { merge: true },
      Form: { merge: true },
      Group: { merge: true },
      GroupMember: { merge: true },
      GroupRole: { merge: true },
      GroupRolePermission: { merge: true },
      Organisation: { merge: true },
      OrganisationMember: { merge: true },
      OrganisationRole: { merge: true },
      OrganisationRolePermission: { merge: true },
      Page: { merge: true },
      Project: { merge: true },
      ProjectMember: { merge: true },
      ProjectRole: { merge: true },
      ProjectRolePermission: { merge: true },
      Question: { merge: true },
      Section: { merge: true },
      Table: { merge: true },
      User: { merge: true },
    },
  });

  persistCache({
    cache,
    storage: new LocalStorageWrapper(window.localStorage),
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const basic = setContext((operation, context) => ({
    headers: {
      Accept: 'charset=utf-8',
    },
  }));

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const auth = setContext(async (operation, context) => {
    const idToken = await authenticationService.getIdToken();

    if (idToken === null) return {};
    return {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    };
  });
  // Middleware for dealing with Apollo requests
  const link = ApolloLink.from([
    basic,
    auth,
    trackerLink,
    jobLinkTracker,
    offlineLinkTracker,
    serializingLink as unknown as ApolloLink,
    new RetryLink(),
    errorLink,
    httpLink.create({ uri: URL }),
  ]);

  return {
    link,
    cache,
    defaultOptions: {
      watchQuery: {
        // Apollo Client v4 changed notifyOnNetworkStatusChange default to true,
        // which emits an initial { loading: true, data: undefined } before actual data.
        // This breaks 128 instances of .valueChanges.pipe(take(1)) across 77 files.
        // TODO: Migrate all watchQuery+take(1) one-shot patterns to either:
        //   1. Add filter(result => !result.loading) before take(1) in each file, OR
        //   2. Replace watchQuery+take(1) with apollo.client.query() (returns Promise, no loading emission)
        // Once migrated, remove this override to benefit from v4's loading state notifications.
        notifyOnNetworkStatusChange: false,
      },
    },
  };
}

@NgModule({
  exports: [HttpClientModule],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, AuthenticationService],
    },
  ],
})
export class GraphQLModule {}
